// Month options and store bridging for the Activity Log's period selector.
//
// The selector on /data-pipeline?tab=activity used to be the legacy PeriodSelector — a
// free date-range picker in its own sub-toolbar. This replaces it with the control
// /financial-control uses: a FilterDropdown of whole months, in the shared header's row 2.
//
// ActivityLog itself is deliberately untouched. It reads startDate/endDate from
// filterStore, so the month picker writes that same window (the month's first and last
// day) and every consumer downstream keeps working unchanged.
import dayjs from "dayjs";

// PERIOD_MONTHS matches /financial-control's twelve-month window, so the two pages offer
// the same range rather than each inventing one.
export const PERIOD_MONTHS = 12;

// periodOptions lists the last twelve months, newest first, as
// {value:"YYYY-MM", label:"June 2026"} — the same shape and formatting
// FinancialControlPage builds.
export function periodOptions(now = dayjs()) {
  return Array.from({ length: PERIOD_MONTHS }, (_, i) => {
    const d = now.subtract(i, "month");
    return { value: d.format("YYYY-MM"), label: d.format("MMMM YYYY") };
  });
}

// monthFromDate reads the selected month back out of the stored window, so the dropdown
// reflects the store rather than holding a second copy of the same state that could drift.
export function monthFromDate(startDate) {
  if (!startDate) return dayjs().format("YYYY-MM");
  const d = dayjs.isDayjs(startDate) ? startDate : dayjs(startDate);
  return d.isValid() ? d.format("YYYY-MM") : dayjs().format("YYYY-MM");
}

// MONTH_RE validates the month BEFORE dayjs sees it. dayjs parses leniently and rolls
// overflow forward, so dayjs("2026-13-01").isValid() is true and quietly means January
// 2027 — the list would then show a different month than the one selected. A format check
// is the only thing that catches it.
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// monthRange converts "YYYY-MM" into the inclusive day window ActivityLog queries with.
// Returns null for anything malformed rather than a window around today — silently
// substituting a different period would misreport which month is on screen.
export function monthRange(month) {
  if (typeof month !== "string" || !MONTH_RE.test(month)) return null;
  const start = dayjs(`${month}-01`);
  if (!start.isValid()) return null;
  return { startDate: start.startOf("month"), endDate: start.endOf("month") };
}
